import { appIntl } from '@components/locale/IntlGlobalProvider';
import { useMapContext } from '@components/map/MapContext';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { switchDrawModeByMapId, updateMenuNavigationStatus } from '@redux/maps/maps.reducer';
import { selectMapNavigationOptions } from '@redux/maps/maps.selectors';
import { useCallback } from 'react';

import { Coordinates } from '@/types/commons/commons.types';

interface UseMeasureFromPointMenuItemProps {
  position: Coordinates | null;
  closeContextMenu: () => void;
  measureOrigin: MeasureOriginEnum;
}

export enum MeasureOriginEnum {
  POINT = 'POINT',
  CAMERA = 'CAMERA',
  API = 'API',
  BT = 'BT',
  PERIMETER_SECTION = 'PERIMETER_SECTION',
  EVENT = 'EVENT',
  OTHER_SENSOR = 'OTHER_SENSOR',
}

export const useMeasureFromPointMenuItem = ({
  closeContextMenu,
  position,
  measureOrigin,
}: UseMeasureFromPointMenuItemProps): [label: string, enableMeasure: () => void] => {
  const { formatMessage } = appIntl();
  const { mapId, setMeasurePoints, setIsClickEnabled, setIsMeasureActive } = useMapContext();

  const selectedToolMenuOptions = useSelectorWithMapId(selectMapNavigationOptions);
  const dispatchSelectedOptions = useWithDispatch(updateMenuNavigationStatus);
  const dispatchSwitchDrawModeByMapId = useWithDispatch(switchDrawModeByMapId);

  //Closes all draw modes
  const enableMeasure = useCallback(() => {
    if (position?.longitude && position.latitude) {
      const firstPoint = [position.longitude, position.latitude];
      setMeasurePoints([firstPoint]);
      setIsClickEnabled(true);
      setIsMeasureActive(true);

      let newSelectedOptions = structuredClone(selectedToolMenuOptions ?? []);
      if (newSelectedOptions.includes('create')) {
        newSelectedOptions = newSelectedOptions.filter((opt) => opt !== 'create');
      }
      dispatchSelectedOptions({ mapId, selectedOptions: newSelectedOptions });
      dispatchSwitchDrawModeByMapId({ mapId, isDrawable: false });

      closeContextMenu();
    }
  }, [
    position,
    setMeasurePoints,
    setIsClickEnabled,
    setIsMeasureActive,
    selectedToolMenuOptions,
    dispatchSelectedOptions,
    mapId,
    dispatchSwitchDrawModeByMapId,
    closeContextMenu,
  ]);

  return [formatMessage({ id: 'contextmenu.actions.measure' }, { measureOrigin: measureOrigin }), enableMeasure];
};
